'use client';

import { createClient } from './client';

export interface AppUser {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer' | 'user';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  lastLoginAt: string | null;
  permissions?: {
    can_create_users: boolean;
    can_edit_users: boolean;
    can_delete_users: boolean;
    can_view_logs: boolean;
    can_manage_roles?: boolean;
  };
}

export interface ActivityLog {
  id: string;
  adminUserId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  entityName: string | null;
  details: any;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

function convertToAppUser(row: any): AppUser {
  return {
    id: row.id,
    username: row.username,
    email: row.email,
    role: row.role,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    lastLoginAt: row.last_login_at,
    permissions: row.permissions,
  };
}

function convertToActivityLog(row: any): ActivityLog {
  return {
    id: row.id,
    adminUserId: row.admin_user_id,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    entityName: row.entity_name,
    details: row.details,
    ipAddress: row.ip_address,
    userAgent: row.user_agent,
    createdAt: row.created_at,
  };
}

export const adminAuthService = {
  async login(username: string, password: string): Promise<{ success: boolean; user?: AppUser; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { data, error } = await supabase.rpc('admin_login', {
        p_username: username,
        p_password: password
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Invalid credentials' };
      }

      const user = convertToAppUser(data[0]);
      return { success: true, user };
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  },

  async getAllAppUsers(): Promise<AppUser[]> {
    try {
      const supabase = createClient();
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(convertToAppUser) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async createAppUser(userData: {
    username: string;
    email: string;
    password: string;
    role?: 'admin' | 'editor' | 'viewer' | 'user';
    createdBy: string;
  }): Promise<{ success: boolean; user?: AppUser; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { data, error } = await supabase.rpc('create_app_user', {
        p_username: userData.username,
        p_email: userData.email,
        p_password: userData.password,
        p_role: userData.role || 'user',
        p_created_by: userData.createdBy
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Failed to create user' };
      }

      const user = convertToAppUser(data[0]);
      return { success: true, user };
    } catch (error: any) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  async updateAppUserPassword(userId: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase.rpc('update_app_user_password', {
        p_user_id: userId,
        p_new_password: newPassword
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Update password error:', error);
      throw error;
    }
  },

  async updateAppUserRole(userId: string, newRole: 'admin' | 'editor' | 'viewer' | 'user'): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('app_users')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Update role error:', error);
      throw error;
    }
  },

  async toggleAppUserStatus(userId: string, isActive: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('app_users')
        .update({ is_active: isActive })
        .eq('id', userId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Toggle status error:', error);
      throw error;
    }
  },

  async deleteAppUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('app_users')
        .delete()
        .eq('id', userId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  async getUserActivityLogs(userId: string, limit: number = 50): Promise<ActivityLog[]> {
    try {
      const supabase = createClient();
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('user_activity_logs')
        .select('*')
        .eq('admin_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(convertToActivityLog) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};
function AdminUser(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: AdminUser is not implemented yet.', args);
  return null;
}

export { AdminUser };